import type { Patient, PatientGender, PatientInput, PatientType, PatientCoverageType } from "./types";
import { patientFullName } from "./types";
import {
  apiCreatePatient,
  apiDeletePatient,
  apiListPatients,
  apiSyncPatient,
  apiUpdatePatient,
} from "@/core/hospitol-api-client";
import {
  markPharmacySotMigratedIfRemoteConfirmed,
  probeHospitolDurableConfigured,
  shouldSeedPharmacyDemoData,
} from "@/core/pharmacy/pharmacy-durable-sot";
import { readJson, writeJson, getStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";
import { shouldAutoSeedDemoData } from "@/core/demo/is-demo-mode-enabled";

const PATIENTS_KEY = "hospitol.patients";
const PATIENT_SEQ_KEY = "hospitol.patientSeq";
const DEMO_SEED_KEY = "hospitol.patients.demoSeeded";

const VALID_GENDERS = new Set<PatientGender>(["male", "female", "other"]);
const VALID_TYPES = new Set<PatientType>(["outpatient", "inpatient"]);
const VALID_COVERAGE = new Set<PatientCoverageType>(["none", "insurer", "company"]);

function replacePatientsCache(patients: Patient[]): void {
  writeJson(PATIENTS_KEY, patients);
  const maxSeq = patients.reduce((max, p) => {
    const match = /^PAT-(\d+)$/.exec(p.fileNumber);
    return match ? Math.max(max, Number(match[1])) : max;
  }, 0);
  if (maxSeq > 0) {
    setStorageItem(PATIENT_SEQ_KEY, String(maxSeq));
  }
}

function replicateToApi(patient: Patient): void {
  void apiSyncPatient(patient);
}

function nextFileNumber(): string {
  const seq = Number(getStorageItem(PATIENT_SEQ_KEY) ?? "0") + 1;
  setStorageItem(PATIENT_SEQ_KEY, String(seq));
  return `PAT-${String(seq).padStart(5, "0")}`;
}

/** Compatibilité dossiers créés avant genre / type patient. */
function normalizePatient(raw: Record<string, unknown>): Patient {
  const gender = raw.gender;
  const patientType = raw.patientType;
  const coverageType = raw.coverageType;

  return {
    id: String(raw.id ?? ""),
    fileNumber: String(raw.fileNumber ?? ""),
    firstName: String(raw.firstName ?? ""),
    lastName: String(raw.lastName ?? ""),
    gender: VALID_GENDERS.has(gender as PatientGender) ? (gender as PatientGender) : "other",
    patientType: VALID_TYPES.has(patientType as PatientType)
      ? (patientType as PatientType)
      : "outpatient",
    coverageType: VALID_COVERAGE.has(coverageType as PatientCoverageType)
      ? (coverageType as PatientCoverageType)
      : "none",
    coverageName: String(raw.coverageName ?? ""),
    insurerId: raw.insurerId ? String(raw.insurerId) : null,
    insurerAgentId: raw.insurerAgentId ? String(raw.insurerAgentId) : null,
    memberNumber: String(raw.memberNumber ?? ""),
    policyNumber: String(raw.policyNumber ?? ""),
    phone: String(raw.phone ?? ""),
    address: String(raw.address ?? ""),
    birthDate: String(raw.birthDate ?? ""),
    notes: String(raw.notes ?? ""),
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
    updatedAt: String(raw.updatedAt ?? new Date().toISOString()),
  };
}

function readPatients(): Patient[] {
  const raw = readJson<Record<string, unknown>[]>(PATIENTS_KEY, []);
  return raw.map(normalizePatient);
}

export function ensureDemoPatients() {
  if (!shouldAutoSeedDemoData()) return;
  if (getStorageItem(DEMO_SEED_KEY)) return;

  const now = new Date().toISOString();
  const demos: PatientInput[] = [
    {
      firstName: "Jean",
      lastName: "Mukendi",
      gender: "male",
      patientType: "outpatient",
      phone: "+243 81 234 5678",
      address: "12 Av. Lumumba, Kinshasa",
      birthDate: "1985-03-15",
    },
    {
      firstName: "Marie",
      lastName: "Kabila",
      gender: "female",
      patientType: "inpatient",
      phone: "+243 99 876 5432",
      address: "45 Q. Gombe, Kinshasa",
      birthDate: "1992-11-08",
    },
    {
      firstName: "Paul",
      lastName: "Ilunga",
      gender: "male",
      patientType: "outpatient",
      phone: "+243 82 111 2233",
      address: "8 Rue Commerce, Lubumbashi",
      birthDate: "1978-07-22",
    },
  ];

  for (const input of demos) {
    createPatient(input);
  }

  setStorageItem(DEMO_SEED_KEY, now);
}

export function listPatients(): Patient[] {
  // WP-H2.1: seed only when probe explicitly says durable is OFF (never invent into durable SoT).
  if (shouldSeedPharmacyDemoData()) {
    ensureDemoPatients();
  }
  return readPatients().sort((a, b) => a.lastName.localeCompare(b.lastName, "fr"));
}

/** Télécharge les patients depuis l'API (SoT si durable) et met à jour le cache local. */
export async function syncPatientsFromApi(): Promise<boolean> {
  const remote = await apiListPatients();
  if (remote === null) return false;

  const durable = await probeHospitolDurableConfigured();

  if (remote.length > 0) {
    replacePatientsCache(remote);
    markPharmacySotMigratedIfRemoteConfirmed("patients", durable, remote.length);
    return true;
  }

  const local = readPatients();
  if (local.length > 0) {
    // WP-H2.1: remote empty + local populated → push LS→API (even if migrate flag was set).
    // Never clear local cache when remote is empty (no silent legacy discard).
    for (const patient of local) {
      await apiSyncPatient(patient);
    }
    const again = await apiListPatients();
    if (again === null) return false;
    if (again.length > 0) {
      replacePatientsCache(again);
      markPharmacySotMigratedIfRemoteConfirmed("patients", durable, again.length);
    }
    return true;
  }

  return true;
}

export function getPatientById(id: string): Patient | null {
  return listPatients().find((p) => p.id === id) ?? null;
}

export function searchPatients(query: string): Patient[] {
  const q = query.trim().toLowerCase();
  if (!q) return listPatients();
  return listPatients().filter(
    (p) =>
      patientFullName(p).toLowerCase().includes(q) ||
      p.fileNumber.toLowerCase().includes(q) ||
      p.phone.toLowerCase().includes(q) ||
      p.address.toLowerCase().includes(q),
  );
}

function buildPatientFields(input: PatientInput): Pick<
  Patient,
  | "firstName"
  | "lastName"
  | "gender"
  | "patientType"
  | "coverageType"
  | "coverageName"
  | "insurerId"
  | "insurerAgentId"
  | "memberNumber"
  | "policyNumber"
  | "phone"
  | "address"
  | "birthDate"
  | "notes"
> {
  const coverageType = input.coverageType ?? "none";
  const insurerId =
    coverageType === "insurer" && input.insurerId ? input.insurerId : null;
  return {
    firstName: input.firstName.trim(),
    lastName: input.lastName.trim(),
    gender: input.gender,
    patientType: input.patientType,
    coverageType,
    coverageName: coverageType === "none" ? "" : (input.coverageName?.trim() ?? ""),
    insurerId,
    insurerAgentId:
      insurerId && input.insurerAgentId ? input.insurerAgentId : null,
    memberNumber: input.memberNumber?.trim() ?? "",
    policyNumber: input.policyNumber?.trim() ?? "",
    phone: input.phone.trim(),
    address: input.address.trim(),
    birthDate: input.birthDate?.trim() ?? "",
    notes: input.notes?.trim() ?? "",
  };
}

export function createPatient(input: PatientInput): Patient {
  const now = new Date().toISOString();
  const patient: Patient = {
    id: `pat-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    fileNumber: nextFileNumber(),
    ...buildPatientFields(input),
    createdAt: now,
    updatedAt: now,
  };
  const list = readPatients();
  list.unshift(patient);
  writeJson(PATIENTS_KEY, list);
  void apiCreatePatient(input).then((apiPatient) => {
    if (!apiPatient) {
      replicateToApi(patient);
      return;
    }
    const current = readPatients();
    const index = current.findIndex((p) => p.id === patient.id);
    if (index >= 0) {
      current[index] = apiPatient;
      writeJson(PATIENTS_KEY, current);
    }
  });
  return patient;
}

export function updatePatient(id: string, input: PatientInput): Patient | null {
  const list = readPatients();
  const index = list.findIndex((p) => p.id === id);
  if (index < 0) return null;

  const updated: Patient = {
    ...list[index]!,
    ...buildPatientFields(input),
    updatedAt: new Date().toISOString(),
  };
  list[index] = updated;
  writeJson(PATIENTS_KEY, list);
  void apiUpdatePatient(id, input).then((apiPatient) => {
    if (!apiPatient) {
      replicateToApi(updated);
      return;
    }
    const current = readPatients();
    const idx = current.findIndex((p) => p.id === id);
    if (idx >= 0) {
      current[idx] = apiPatient;
      writeJson(PATIENTS_KEY, current);
    }
  });
  return updated;
}

export function listPatientsByInsurer(insurerId: string): Patient[] {
  return listPatients().filter((p) => p.insurerId === insurerId);
}

export function deletePatient(id: string): boolean {
  const list = readPatients();
  const next = list.filter((p) => p.id !== id);
  if (next.length === list.length) return false;
  writeJson(PATIENTS_KEY, next);
  void apiDeletePatient(id);
  return true;
}

export function patientDetailPath(id: string): string {
  return `/tenant/hospitol/patients/${id}`;
}
